/**
 * `AgentOperatorsView` — ADR-0234 ops2026 Agents → Agent companies.
 *
 * AI companies ("operators") enrol their agents with Sill: verified
 * domain, agent identities (user-agent names + docs page on that domain)
 * and Ed25519 public keys. The portal is invitation-only, so staff
 * invite AI companies here (one email + one button → the operator gets a
 * sign-in link). Enrollments then start pending; until staff approve,
 * none of their keys verify anywhere. Here staff:
 *   - invite an AI company by email (idempotent — re-sends the link if
 *     an operator already exists);
 *   - approve (also approves the identities submitted with it) or reject
 *     (note required) a pending enrollment;
 *   - approve / reject identities an approved company adds later;
 *   - suspend (note required) or reinstate a company;
 *   - revoke a key in an emergency.
 * Opening a company is staff-audited. Every key change is also in the
 * company's public, hash-chained key history.
 */

import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type {
  AdminAgentOperatorDetailResponse,
  AdminAgentOperatorsResponse,
  AdminOperatorReviewAction,
  AgentOperatorStatus,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Pill } from '../../components/atoms/Pill.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

type Tab = 'pending' | 'approved' | 'suspended' | 'all';

const STATUS_TONE: Readonly<Record<AgentOperatorStatus, 'neutral' | 'lime' | 'amber' | 'coral' | 'sky' | 'mint'>> = {
  draft: 'neutral',
  pending: 'sky',
  approved: 'mint',
  rejected: 'coral',
  suspended: 'coral',
};

export function AgentOperatorsView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [tab, setTab] = useState<Tab>('pending');
  const [data, setData] = useState<AdminAgentOperatorsResponse | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMessage, setInviteMessage] = useState<{ readonly kind: 'ok' | 'err'; readonly text: string } | null>(null);
  // Bug #18 — enrollment mode toggle (admin-only). Null while loading.
  const [enrollmentMode, setEnrollmentMode] = useState<'open' | 'invite_only' | null>(null);
  const [modeBusy, setModeBusy] = useState(false);
  const [modeConfirm, setModeConfirm] = useState(false);
  const [modeError, setModeError] = useState<string | null>(null);
  const [modeLoadError, setModeLoadError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.getAgentOperatorEnrollmentMode();
        setEnrollmentMode(r.mode);
      } catch (err) {
        // Non-admins get 403: hide the control. Anything else is a real
        // failure and must be visible, not a silently missing toggle.
        if (err instanceof ApiError && err.status === 403) return;
        setModeLoadError(err instanceof ApiError ? err.message : 'Could not load the enrollment mode.');
      }
    })();
  }, [api]);

  const flipMode = useCallback(async (next: 'open' | 'invite_only'): Promise<void> => {
    setModeBusy(true); setModeError(null);
    try {
      const r = await api.setAgentOperatorEnrollmentMode(next);
      setEnrollmentMode(r.mode);
      setModeConfirm(false);
    } catch (err) {
      setModeError(err instanceof ApiError ? err.message : 'Could not change enrollment mode.');
    } finally { setModeBusy(false); }
  }, [api]);

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      setData(await api.getAgentOperators(tab));
      setMissing(false);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'not_found') setMissing(true);
      else setError(err instanceof ApiError ? err.message : 'Failed to load agent companies.');
    }
  }, [api, tab]);

  useEffect(() => { void load(); }, [load]);

  const invite = useCallback(async (): Promise<void> => {
    const email = inviteEmail.trim();
    if (email.length === 0) return;
    setInviteBusy(true);
    setInviteMessage(null);
    try {
      const r = await api.inviteAgentOperator(email);
      setInviteMessage({ kind: 'ok', text: r.created ? `Invited ${email}. A sign-in link is on its way.` : `${email} already exists. Re-sent the sign-in link.` });
      setInviteEmail('');
      await load();
    } catch (err) {
      const msg = err instanceof ApiError
        ? (err.kind === 'rate_limited'
            ? 'Too many invites in the last hour. Try again later.'
            : err.kind === 'bad_request'
              ? 'Enter a valid email address.'
              : err.message)
        : 'The invite failed.';
      setInviteMessage({ kind: 'err', text: msg });
    } finally {
      setInviteBusy(false);
    }
  }, [api, inviteEmail, load]);

  const kicker = { fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase' as const };

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ ...kicker, marginBottom: 8 }}>ADR-0234 · Sill agent credentials</div>
          <h1 style={{ fontFamily: serifFont, fontSize: 28, color: C.text, margin: 0 }}>Agent companies</h1>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, marginTop: 4, maxWidth: 720, lineHeight: 1.5 }}>
            AI companies that enrolled their agents&apos; keys. Check that the domain, the documentation page and the user-agent names belong together before approving: until then their keys are not honoured anywhere.
          </div>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: sansFont, fontSize: 12, padding: '6px 12px', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer' }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {modeLoadError !== null && enrollmentMode === null && (
        <Card style={{ padding: 14, marginBottom: 14 }} data-testid="enrollment-mode-error">
          <div style={{ ...kicker, marginBottom: 6 }}>Enrollment mode</div>
          <div style={{ fontFamily: sansFont, fontSize: 12.5, color: '#A0331E' }}>Could not load the enrollment mode: {modeLoadError}</div>
        </Card>
      )}
      {enrollmentMode !== null && (
        <Card style={{ padding: 14, marginBottom: 14 }} data-testid="enrollment-mode-card">
          <div style={{ ...kicker, marginBottom: 6 }}>Enrollment mode</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Pill tone={enrollmentMode === 'open' ? 'mint' : 'sky'} data-testid="enrollment-mode-pill">
              {enrollmentMode === 'open' ? 'Open to all' : 'Invite only'}
            </Pill>
            {!modeConfirm ? (
              <button
                type="button"
                data-testid="enrollment-mode-toggle"
                onClick={() => setModeConfirm(true)}
                disabled={modeBusy}
                style={{ fontFamily: sansFont, fontSize: 12.5, padding: '6px 12px', background: 'transparent', color: C.text, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer' }}
              >{enrollmentMode === 'open' ? 'Switch to invite only' : 'Open enrollment to all'}</button>
            ) : (
              <>
                <span style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted }}>
                  {enrollmentMode === 'open'
                    ? 'Only invited AI companies can create an account. Sill still reviews every enrollment.'
                    : 'Anyone can create an account. Sill still reviews every enrollment before keys are honoured.'}
                </span>
                <button type="button" data-testid="enrollment-mode-confirm" disabled={modeBusy} onClick={() => { void flipMode(enrollmentMode === 'open' ? 'invite_only' : 'open'); }} style={{ fontFamily: sansFont, fontSize: 12.5, padding: '6px 12px', background: C.lime, color: '#0B0B0C', border: `1px solid ${C.lime}`, borderRadius: 3, cursor: 'pointer' }}>
                  {modeBusy ? 'Switching…' : 'Confirm'}
                </button>
                <button type="button" disabled={modeBusy} onClick={() => setModeConfirm(false)} style={{ fontFamily: sansFont, fontSize: 12.5, padding: '6px 12px', background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer' }}>Cancel</button>
              </>
            )}
          </div>
          {modeError !== null && <div style={{ fontFamily: sansFont, fontSize: 12, color: '#A0331E', marginTop: 8 }}>{modeError}</div>}
        </Card>
      )}
      <Card style={{ padding: 14, marginBottom: 14 }} data-testid="invite-card">
        <div style={{ ...kicker, marginBottom: 6 }}>Invite AI company</div>
        <div style={{ fontFamily: sansFont, fontSize: 12.5, color: C.muted, marginBottom: 8, lineHeight: 1.5 }}>
          {enrollmentMode === 'open'
            ? 'Enrollment is open to all. You can still invite someone directly here; they get a one-time sign-in link.'
            : 'Enrollment is invitation-only. Enter the work email of someone at an AI company; they get a one-time sign-in link.'}
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); void invite(); }}
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}
        >
          <input
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="founder@example.com"
            data-testid="invite-email"
            autoComplete="email"
            style={{ flex: '1 1 240px', minWidth: 240, fontFamily: sansFont, fontSize: 13, padding: '6px 10px', background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 3 }}
          />
          <button
            type="submit"
            disabled={inviteBusy || inviteEmail.trim().length === 0}
            data-testid="invite-submit"
            style={{ fontFamily: sansFont, fontSize: 12, padding: '6px 14px', borderRadius: 3, cursor: 'pointer', background: C.text, color: C.bg, border: 'none', opacity: inviteBusy || inviteEmail.trim().length === 0 ? 0.5 : 1 }}
          >{inviteBusy ? 'Sending…' : 'Send sign-in link'}</button>
        </form>
        {inviteMessage !== null ? (
          <div
            role={inviteMessage.kind === 'err' ? 'alert' : 'status'}
            data-testid={`invite-${inviteMessage.kind}`}
            style={{ marginTop: 8, fontFamily: sansFont, fontSize: 12.5, color: inviteMessage.kind === 'err' ? C.coralFg : C.body }}
          >{inviteMessage.text}</div>
        ) : null}
      </Card>

      <div role="tablist" aria-label="Company status" style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        {(['pending', 'approved', 'suspended', 'all'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => { setTab(t); setSelected(null); }}
            style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, textTransform: 'uppercase', padding: '6px 10px', background: 'transparent', border: 'none', borderBottom: `2px solid ${tab === t ? C.text : 'transparent'}`, color: tab === t ? C.text : C.muted, cursor: 'pointer' }}
          >
            {t === 'pending' ? `Needs review${data !== null ? ` (${data.pending_count})` : ''}` : t}
          </button>
        ))}
      </div>

      {missing ? (
        <Card style={{ padding: 16 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13.5, color: C.body }}>The agent-company API is not deployed yet. This page will work once it is live.</div>
        </Card>
      ) : null}
      {error !== null ? <Card style={{ padding: 16, marginBottom: 12 }}><div role="alert" style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div></Card> : null}
      {data !== null && data.operators.length === 0 ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }} data-testid="operators-empty">{tab === 'pending' ? 'Nothing to review.' : 'No companies here yet.'}</div>
      ) : null}

      {data !== null && data.operators.length > 0 ? (
        <Card style={{ padding: 0, marginBottom: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont, fontSize: 13 }}>
            <thead>
              <tr>{['Company', 'Domain', 'Status', 'Pending agents', 'Active keys', 'Submitted'].map((h) => (
                <th key={h} style={{ ...kicker, textAlign: 'left', padding: '10px 12px', borderBottom: `1px solid ${C.border}` }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {data.operators.map((o) => (
                <tr
                  key={o.operator_id}
                  data-testid={`operator-${o.operator_id}`}
                  onClick={() => setSelected(o.operator_id)}
                  style={{ cursor: 'pointer', background: selected === o.operator_id ? C.surface2 : 'transparent' }}
                >
                  <td style={{ padding: '10px 12px', color: C.text }}>{o.display_name ?? o.email}<div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>{o.slug ?? '—'}</div></td>
                  <td style={{ padding: '10px 12px', color: C.body }}>{o.domain ?? '—'}{o.domain_verified_method !== null ? <span style={{ fontFamily: monoFont, fontSize: 10, color: C.muted }}> · {o.domain_verified_method}</span> : null}</td>
                  <td style={{ padding: '10px 12px' }}><Pill tone={STATUS_TONE[o.status]}>{o.status}</Pill></td>
                  <td style={{ padding: '10px 12px', color: C.body }}>{o.agents_pending}</td>
                  <td style={{ padding: '10px 12px', color: C.body }}>{o.keys_active}</td>
                  <td style={{ padding: '10px 12px', color: C.muted, fontFamily: monoFont, fontSize: 11 }}>{o.submitted_at !== null ? formatIsoCompact(o.submitted_at) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      {selected !== null ? <OperatorDetail operatorId={selected} onChanged={() => void load()} /> : null}
    </div>
  );
}

function OperatorDetail({ operatorId, onChanged }: { readonly operatorId: string; readonly onChanged: () => void }): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [d, setD] = useState<AdminAgentOperatorDetailResponse | null>(null);
  const [note, setNote] = useState('');
  const [confirmRegistry, setConfirmRegistry] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      setD(await api.getAgentOperator(operatorId));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load the company.');
    }
  }, [api, operatorId]);
  useEffect(() => { void load(); }, [load]);

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setNote('');
      await load();
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'The action failed.');
    } finally {
      setBusy(false);
    }
  };
  const review = (a: AdminOperatorReviewAction): Promise<void> => act(() => api.reviewAgentOperator(operatorId, a, note.trim(), confirmRegistry));

  if (d === null) {
    return <Card style={{ padding: 16 }}>{error !== null ? <div role="alert" style={{ color: C.coralFg, fontFamily: sansFont, fontSize: 13 }}>{error}</div> : <div style={{ color: C.muted, fontFamily: sansFont, fontSize: 13 }}>Loading…</div>}</Card>;
  }
  const o = d.operator;
  const small = { fontFamily: monoFont, fontSize: 11, color: C.muted };
  const h2 = { fontFamily: serifFont, fontSize: 20, color: C.text, margin: '16px 0 6px' };
  const btn = (label: string, onClick: () => void, opts: { danger?: boolean; disabled?: boolean; testid?: string } = {}) => (
    <button
      type="button"
      disabled={busy || opts.disabled === true}
      data-testid={opts.testid}
      onClick={onClick}
      style={{ fontFamily: sansFont, fontSize: 12, padding: '6px 12px', borderRadius: 3, cursor: 'pointer', background: opts.danger === true ? 'transparent' : C.text, color: opts.danger === true ? C.coralFg : C.bg, border: opts.danger === true ? `1px solid ${C.coralFg}` : 'none', opacity: busy || opts.disabled === true ? 0.5 : 1 }}
    >{label}</button>
  );

  return (
    <Card style={{ padding: 18 }} data-testid="operator-detail">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: serifFont, fontSize: 24, color: C.text }}>{o.display_name ?? o.email}</span>
        <Pill tone={STATUS_TONE[o.status]}>{o.status}</Pill>
        <span style={small}>{o.email}</span>
      </div>
      <div style={{ ...small, marginTop: 6 }}>
        domain {o.domain ?? '—'} {o.domain_verified_method !== null ? `(verified by ${o.domain_verified_method}${o.domain_verified_at !== null ? `, ${formatIsoCompact(o.domain_verified_at)}` : ''})` : '(not verified)'}
        {' · '}security contact {o.security_contact ?? '—'}
      </div>
      {o.signature_agent !== null ? <div style={{ ...small, marginTop: 4 }}>Signature-Agent: &quot;{o.signature_agent}&quot;</div> : null}

      {d.registry_collisions.length > 0 ? (
        <div role="note" data-testid="registry-collisions" style={{ marginTop: 12, padding: 10, border: `1px solid ${C.amberFg}`, borderRadius: 3, fontFamily: sansFont, fontSize: 13, color: C.amberFg }}>
          These names also match agents already in Sill&apos;s registry. Approval is refused unless you confirm this company is the one that runs them:{' '}
          {d.registry_collisions.map((c) => `${c.token} (${c.display_name})`).join(', ')}
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, color: C.text }}>
            <input type="checkbox" data-testid="confirm-registry" checked={confirmRegistry} onChange={(e) => setConfirmRegistry(e.target.checked)} />
            I checked first-party documentation: this company runs these agents.
          </label>
        </div>
      ) : null}
      {d.token_conflicts.length > 0 ? (
        <div role="alert" data-testid="token-conflicts" style={{ marginTop: 12, padding: 10, border: `1px solid ${C.coralFg}`, borderRadius: 3, fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>
          Another approved company already owns: {d.token_conflicts.join(', ')}. Approval will be refused until these names are removed or the other company releases them.
        </div>
      ) : null}

      <h2 style={h2}>Agents</h2>
      {o.agents.length === 0 ? <div style={small}>None.</div> : null}
      {o.agents.map((a) => (
        <div key={a.agent_id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text }}>{a.display_name}</span>
          <span style={small}>{a.product_tokens.join(', ')}</span>
          <a href={a.docs_url} target="_blank" rel="noreferrer noopener" style={{ ...small, color: C.body }}>{a.docs_url}</a>
          <Pill tone={a.status === 'approved' ? 'mint' : a.status === 'pending' ? 'sky' : 'coral'}>{a.status}</Pill>
          {a.status === 'pending' && o.status === 'approved' ? (
            <span style={{ display: 'inline-flex', gap: 6 }}>
              {btn('Approve agent', () => void act(() => api.reviewAgentOperatorAgent(operatorId, a.agent_id, 'approve', confirmRegistry)), { testid: `agent-approve-${a.agent_id}` })}
              {btn('Reject agent', () => void act(() => api.reviewAgentOperatorAgent(operatorId, a.agent_id, 'reject')), { danger: true })}
            </span>
          ) : null}
        </div>
      ))}

      <h2 style={h2}>Keys</h2>
      {o.keys.map((k) => (
        <div key={k.key_id} style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', padding: '6px 0', borderTop: `1px solid ${C.border}` }}>
          <span style={{ ...small, color: C.text }}>{k.thumbprint}</span>
          <Pill tone={k.status === 'active' ? 'mint' : k.status === 'retiring' ? 'sky' : 'coral'}>{k.status}</Pill>
          {k.verifies ? <Pill tone="lime">honoured</Pill> : null}
          {k.added_since_approval && k.status !== 'revoked' && (o.status === 'suspended' || o.status === 'pending') ? (
            <span data-testid={`key-new-${k.key_id}`}><Pill tone="amber">added since last approval</Pill></span>
          ) : null}
          {k.retire_after !== null && k.status === 'retiring' ? <span style={small}>until {formatIsoCompact(k.retire_after)}</span> : null}
          {k.status !== 'revoked' ? btn('Revoke', () => {
            const reason = window.prompt('Reason for revoking this key (recorded):') ?? '';
            if (reason.trim().length > 0) void act(() => api.revokeAgentOperatorKey(operatorId, k.key_id, reason.trim()));
          }, { danger: true }) : null}
        </div>
      ))}
      <div style={{ ...small, marginTop: 8 }}>
        Key history: {d.key_log_ok ? 'chain intact' : 'CHAIN BROKEN — investigate'}{d.key_log_head !== null ? ` · head ${d.key_log_head.slice(0, 16)}…` : ''}
      </div>

      <h2 style={h2}>Decision</h2>
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 1000))}
        rows={2}
        placeholder="Note to the company (required to reject or suspend; shown to them)"
        data-testid="review-note"
        style={{ width: '100%', fontFamily: sansFont, fontSize: 13, padding: 8, background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 3, resize: 'vertical' }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        {o.status === 'pending' ? btn('Approve', () => void review('approve'), { testid: 'review-approve' }) : null}
        {o.status === 'pending' ? btn('Reject', () => void review('reject'), { danger: true, disabled: note.trim().length === 0, testid: 'review-reject' }) : null}
        {o.status === 'approved' ? btn('Suspend', () => void review('suspend'), { danger: true, disabled: note.trim().length === 0, testid: 'review-suspend' }) : null}
        {o.status === 'suspended' ? btn('Reinstate', () => void review('reinstate'), { testid: 'review-reinstate' }) : null}
      </div>
      {o.status === 'suspended' && o.keys.some((k) => k.added_since_approval && k.status !== 'revoked') ? (
        <div role="note" data-testid="reinstate-new-keys" style={{ marginTop: 8, fontFamily: sansFont, fontSize: 12.5, color: C.amberFg }}>
          Keys marked &quot;added since last approval&quot; become honoured again on reinstate. Check them with the company first, or revoke them.
        </div>
      ) : null}
      <div>
      </div>
      {error !== null ? <div role="alert" style={{ marginTop: 8, fontFamily: sansFont, fontSize: 12.5, color: C.coralFg }}>{error}</div> : null}

      <h2 style={h2}>Activity</h2>
      {d.events.slice(0, 20).map((e, i) => (
        <div key={`${e.created_at}-${i}`} style={{ ...small, padding: '3px 0' }}>{formatIsoCompact(e.created_at)} · {e.actor} · {e.action}</div>
      ))}
    </Card>
  );
}
